import React, { useState } from "react";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";

import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";

import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useSidebarLogic from "./Sidebar/SidebarLogic";
import SidebarMenuSection from "./Sidebar/SidebarMenuSection";
import ScanQRModal from "./ScanQRModal";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const [scanOpen, setScanOpen] = useState(false);

  const currentPath = location.pathname;

  const {
    peopleItems,
    membershipItems,
    storeItems,
    openSection,
    toggleSection,
    isActive,
  } = useSidebarLogic(currentPath);

  const canScan =
    user?.role === "admin" || user?.role === "staff";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <Box
        sx={{
          width: { xs: "80%", lg: 220 },
          height: { xs: "auto", lg: "100vh" },
          position: { xs: "relative", lg: "fixed" },
          top: 0,
          left: 0,
          zIndex: 20,
          borderRight: { xs: "none", lg: "1px solid rgba(148,163,184,0.12)" },
          bgcolor: "#0b1220",
          background:
            "linear-gradient(180deg, #0b1220 0%, #0f172a 52%, #111827 100%)",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1.6, md: 1.8, lg: 1.6 },
          py: { xs: 1.6, lg: 1.8 },
        }}
      >
        <Box
          sx={{
            px: 1.2,
            py: 1.25,
            borderRadius: "18px",
            border: "1px solid rgba(148,163,184,0.14)",
            bgcolor: "rgba(255,255,255,0.03)",
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#e2e8f0",
                fontSize: 16.5,
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              NovaXFit
            </Typography>

            <Typography
              sx={{
                color: "rgba(226,232,240,0.56)",
                fontSize: 11.8,
                fontWeight: 500,
                lineHeight: 1.35,
                mt: 0.35,
              }}
            >
              Administración
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            my: 1.8,
            borderColor: "rgba(148,163,184,0.12)",
          }}
        />

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Typography
            sx={{
              color: "rgba(226,232,240,0.32)",
              fontSize: 11.2,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              px: 1,
              mb: 0.8,
            }}
          >
            Menú
          </Typography>

          <List sx={{ p: 0 }}>
            <SidebarMenuSection
              title="Personas"
              icon={<GroupsRoundedIcon sx={{ fontSize: 20 }} />}
              sectionKey="people"
              openSection={openSection}
              toggleSection={toggleSection}
              items={peopleItems}
              navigate={navigate}
              isActive={isActive}
            />

            <SidebarMenuSection
              title="Membresías"
              icon={<CardMembershipRoundedIcon sx={{ fontSize: 20 }} />}
              sectionKey="memberships"
              openSection={openSection}
              toggleSection={toggleSection}
              items={membershipItems}
              navigate={navigate}
              isActive={isActive}
            />

            <SidebarMenuSection
              title="NovaXFit Store"
              icon={<StorefrontRoundedIcon sx={{ fontSize: 20 }} />}
              sectionKey="store"
              openSection={openSection}
              toggleSection={toggleSection}
              items={storeItems}
              navigate={navigate}
              isActive={isActive}
            />

            {canScan && (
              <ListItemButton
                onClick={() => setScanOpen(true)}
                sx={{
                  minHeight: 44,
                  borderRadius: "12px",
                  px: 1,
                  color: "rgba(226,232,240,0.72)",
                  border: "1px solid rgba(148,163,184,0.12)",
                  bgcolor: "transparent",
                  "&:hover": {
                    bgcolor: "rgba(148,163,184,0.08)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 34,
                    color: "inherit",
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 20 }} />
                </ListItemIcon>

                <ListItemText
                  primary="Escanear QR"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: 13.4,
                  }}
                />
              </ListItemButton>
            )}

            <ListItemButton
              onClick={() => navigate("/")}
              sx={{
                minHeight: 44,
                borderRadius: "12px",
                px: 1,
                color: isActive("/") ? "#e2e8f0" : "rgba(226,232,240,0.72)",
                bgcolor: isActive("/")
                  ? "rgba(148,163,184,0.14)"
                  : "transparent",
                border: isActive("/")
                  ? "1px solid rgba(148,163,184,0.18)"
                  : "1px solid transparent",
                "&:hover": {
                  bgcolor: isActive("/")
                    ? "rgba(148,163,184,0.14)"
                    : "rgba(148,163,184,0.08)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 34,
                  color: "inherit",
                }}
              >
                <HomeRoundedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>

              <ListItemText
                primary="Ir a Web"
                primaryTypographyProps={{
                  fontWeight: isActive("/") ? 700 : 600,
                  fontSize: 13.4,
                }}
              />
            </ListItemButton>
          </List>
        </Box>

        <Box sx={{ pt: 1.2 }}>
          <Divider
            sx={{
              mb: 1.2,
              borderColor: "rgba(148,163,184,0.12)",
            }}
          />

          <List sx={{ p: 0 }}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 44,
                borderRadius: "12px",
                px: 1,
                color: "#fca5a5",
                border: "1px solid rgba(248,113,113,0.14)",
                bgcolor: "rgba(239,68,68,0.05)",
                "&:hover": {
                  bgcolor: "rgba(239,68,68,0.10)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 34,
                  color: "inherit",
                }}
              >
                <LogoutRoundedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>

              <ListItemText
                primary="Cerrar sesión"
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: 13.4,
                }}
              />
            </ListItemButton>
          </List>
        </Box>
      </Box>

      <ScanQRModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
      />
    </>
  );
}